import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { getLead, listLeadTasks, listLeads, listStages, type Lead, type LeadFilters } from "../../api/leads";
import { useServices } from "../../app/services";
import { useDirectory } from "../directory/useDirectory";
import { useHomeScope, type Scope } from "../home/hooks";

export type { Scope };
export const useScope = useHomeScope;

export function useStages(sc: Scope) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead-stages", ...sc.scopeKey], queryFn: () => listStages(client), retry: false, staleTime: 5 * 60_000 });
}

/**
 * Staff names for lead owners and the owner pickers, from `GET /directory` (no `staff.view` needed). Without a name
 * lever a lead's owner reads "Staff member" (yourself: your name, else "You") and the pickers are not offered, since
 * they would list nobody by name. Never a fragment of the id.
 */
export function useStaffNames() {
  const dir = useDirectory();
  return useMemo(
    () => ({
      list: dir.people,
      /** Every active person, named or not: for fetching their tasks. */
      activeIds: dir.entries.filter((e) => e.active).map((e) => e.id),
      canList: !dir.error && dir.people.length > 0,
      pending: dir.pending,
      error: dir.error,
      refetch: dir.refetch,
      nameOf: (id: string | null | undefined): string | null => dir.nameOf(id, "staff"),
    }),
    [dir],
  );
}

export function useLeadPages(sc: Scope, filters: LeadFilters, enabled = true) {
  const { client } = useServices();
  const f: LeadFilters = { ...filters, ...(sc.branchId ? { branch: sc.branchId } : {}) };
  return useInfiniteQuery({
    queryKey: ["leads", ...sc.scopeKey, f],
    queryFn: ({ pageParam }) => listLeads(client, f, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.nextCursor,
    enabled,
    retry: false,
  });
}

export const flatten = (pages: ReadonlyArray<{ leads: Lead[] }> | undefined): Lead[] => pages?.flatMap((p) => p.leads) ?? [];

export function useLead(sc: Scope, id: string) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead", ...sc.scopeKey, id], queryFn: () => getLead(client, id), retry: false });
}

export function useLeadTasks(sc: Scope, enabled = true) {
  const { client } = useServices();
  return useQuery({ queryKey: ["lead-tasks", ...sc.scopeKey], queryFn: () => listLeadTasks(client), enabled, retry: false });
}

/** Re-read everything a lead write can change. */
export function useInvalidateLeadData() {
  const qc = useQueryClient();
  return () => Promise.all(["leads", "lead", "lead-tasks", "followups", "appointments"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}
